import './styles.scss';

import { createNotification, handleError } from '@/utils/custom';

import { CategoryGroupLink } from '@/features/Settings/api/services/training/searchQuestionnaireCategoryGroups';
import { updateCategoryGroups } from '@/features/Settings/api/services/training/updateQuestionnaireCategoryGroups';
import { CategoryFormDefinitions } from './types';
import ColorInput from '@/common/ColorInput';
import Form from '@/common/Form';
import Modal from '@/common/Modal';
import { ModalTypes } from '@/common/Modal/types';
import { QuestionnaireCategory } from '@/models/gen/graphql';
import Textarea from '@/common/Textarea';
import { Toast } from '@/models';
import SelectEvaluatorGroups from '@/features/Settings/pages/Categories/components/SelectEvaluatorGroups';
import { createCategoryWithGroups } from '@/features/Settings/api/services/training/createQuestionnaireCategories';
import { deleteCategory } from '@/features/Settings/api/services/training/deleteQuestionnaireCategories';
import { updateCategory } from '@/features/Settings/api/services/training/updateQuestionnaireCategories';
import useConfirmation from '@/hooks/useConfirmation';
import { useMemo } from 'react';

export const CATEGORY_COLOR_SWATCHES = ['#c00403', '#9403fe', '#93d500', '#1b3e6f', '#ff9e16', '#0f9d58', '#4059ff'];

type CategoryModalProps = Omit<ModalTypes.Props, 'title'> & {
  category?: QuestionnaireCategory;
  groupLinks?: CategoryGroupLink[];
  roleId: string;
  onSubmit: () => void;
};

const CategoryModal = ({ show, category, groupLinks = [], roleId, onHide, onSubmit, ...modalProps }: CategoryModalProps): JSX.Element => {
  const isEdit = Boolean(category?.id);

  const confirmDelete = useConfirmation({
    Body: {
      title: 'Delete Category',
      message: `Are you sure you want to delete "${category?.name}"? It will no longer be available for new evaluations.`,
    },
    Footer: { resolve: 'DELETE PERMANENTLY', reject: 'CANCEL' },
  });

  const value = useMemo(
    (): CategoryFormDefinitions.Value =>
      isEdit && category
        ? {
            name: category.name,
            description: category.description,
            color: category.color,
            groupIds: groupLinks.map(({ groupId }) => groupId),
          }
        : CategoryFormDefinitions.initValue,
    [isEdit, category, groupLinks]
  );

  const handleSubmit = async (data: CategoryFormDefinitions.Value): Promise<void> => {
    const title = isEdit ? 'Update Category' : 'Create Category';
    try {
      if (isEdit) {
        await updateCategory([{ id: category.id, name: data.name, description: data.description, color: data.color }]);
        await updateCategoryGroups(category.id, groupLinks, data.groupIds || []);
      } else {
        await createCategoryWithGroups({ roleId, name: data.name, description: data.description, color: data.color }, data.groupIds || []);
      }
      createNotification('Success', Toast.Type.SUCCESS, title);
      onSubmit();
      onHide();
    } catch (err) {
      handleError(err, { notification: { title } });
    }
  };

  const handleDelete = async (): Promise<void> => {
    try {
      await confirmDelete();
    } catch {
      return;
    }
    try {
      await deleteCategory([category.id]);
      createNotification('Success', Toast.Type.SUCCESS, 'Delete Category');
      onSubmit();
      onHide();
    } catch (err) {
      handleError(err, { notification: { title: 'Delete Category' } });
    }
  };

  return (
    <Modal
      {...modalProps}
      show={show}
      onHide={onHide}
      title={isEdit ? 'Edit Category' : 'Add New Category'}
      centered
      className="CategoryModal"
    >
      <Form
        name="categoryModal"
        className="CategoryModal-Form"
        value={value}
        validator={CategoryFormDefinitions.validator}
        onSubmit={handleSubmit}
      >
        <Form.Field name="name" label="Name">
          <Form.Input name="name" validate />
        </Form.Field>
        <Form.Field name="description" label="Description">
          <Form.Input as={Textarea} name="description" rows={4} validate />
        </Form.Field>
        <Form.Field name="color" label="Color">
          <Form.Input as={ColorInput} name="color" swatches={CATEGORY_COLOR_SWATCHES} validate />
        </Form.Field>
        <Form.Field name="groupIds" label="Evaluator Groups">
          <Form.Watch name="groupIds">
            {([groupIds = []], form) => (
              <SelectEvaluatorGroups
                name="groupIds"
                value={groupIds as string[]}
                onChange={(value: string[]): void => form.setValue('groupIds', value, { shouldDirty: true })}
              />
            )}
          </Form.Watch>
        </Form.Field>
        <small className="CategoryModal-Hint">Only these groups can evaluate this category. Leave empty to allow any evaluator.</small>
        <div className="CategoryModal-Footer">
          <Form.Button type="button" variant="secondary" onClick={onHide}>
            Cancel
          </Form.Button>
          {isEdit && (
            <Form.Button type="button" variant="danger" onClick={handleDelete}>
              Delete
            </Form.Button>
          )}
          <Form.Button type="submit" variant="primary">
            Save
          </Form.Button>
        </div>
      </Form>
    </Modal>
  );
};

export default CategoryModal;
